import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Wrench, Plus, Trash2 } from "lucide-react";

const TYPES = ["Service", "Repair", "Tire Change", "Oil Change", "Brakes", "Cleaning / Detail", "Inspection", "Other"];
const STATUSES = ["Completed", "Scheduled"];
const EMPTY_FORM = { vehicle_id: "", type: "Service", date: "", cost: "", description: "", status: "Completed" };
const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

export default function MaintenanceTab() {
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/maintenance").then((r) => setRecords(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.vehicle_id || !form.date) return toast.error("Vehicle and date are required");
    setSaving(true);
    try {
      await api.post("/admin/maintenance", {
        vehicle_id: form.vehicle_id,
        type: form.type,
        date: form.date,
        cost: parseFloat(form.cost || "0"),
        description: form.description,
        status: form.status,
      });
      toast.success("Maintenance record saved");
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this ${r.type} record for ${r.vehicle_name}?`)) return;
    try {
      await api.delete(`/admin/maintenance/${r.id}`);
      toast.success("Record deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totalCost = records.reduce((s, r) => s + (r.cost || 0), 0);

  return (
    <div data-testid="admin-maintenance-tab">
      <AdminHeader testid="admin-header-maintenance" eyebrow="Fleet" title="Maintenance" description="Track service, repairs and upkeep spend." />

      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <Wrench size={13} className="text-[#C71E2E]" /> Log service, repair, tire change & more
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Vehicle</Label>
            <Select value={form.vehicle_id} onValueChange={(val) => setForm({ ...form, vehicle_id: val })}>
              <SelectTrigger data-testid="maintenance-form-vehicle" className="input-dark mt-1.5"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
              <SelectContent className="bg-white border-[#E5DED5]">
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Type</Label>
            <Select value={form.type} onValueChange={(val) => setForm({ ...form, type: val })}>
              <SelectTrigger data-testid="maintenance-form-type" className="input-dark mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#E5DED5]">
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Date</Label>
            <Input data-testid="maintenance-form-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark mt-1.5" />
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Cost (USD)</Label>
            <Input data-testid="maintenance-form-cost" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" className="input-dark mt-1.5" />
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Status</Label>
            <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
              <SelectTrigger data-testid="maintenance-form-status" className="input-dark mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#E5DED5]">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <Label className="font-ui text-xs text-muted-foreground">Description</Label>
            <Textarea data-testid="maintenance-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Front tires replaced, alignment done" className="input-dark mt-1.5" rows={1} />
          </div>
        </div>
        <Button data-testid="maintenance-submit-button" onClick={save} disabled={saving} className="mt-5 rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
          <Plus size={15} className="mr-2" /> {saving ? "Saving…" : "Add Record"}
        </Button>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-muted-foreground font-ui">{records.length} records</p>
        <p data-testid="maintenance-total-cost" className="text-sm font-ui text-muted-foreground">Total maintenance spend: <span className="text-[#C71E2E] font-semibold">{fmtMoney(totalCost)}</span></p>
      </div>

      <div className="mt-3 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Date</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Cost</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow className={ROW}>
                <TableCell colSpan={7} className="text-muted-foreground font-ui py-10 text-center">No maintenance records yet.</TableCell>
              </TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`maintenance-row-${r.id}`} className={ROW}>
                <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(r.date)}</TableCell>
                <TableCell className="font-ui font-medium whitespace-nowrap">{r.vehicle_name}</TableCell>
                <TableCell className="whitespace-nowrap">{r.type}</TableCell>
                <TableCell className="text-muted-foreground max-w-[280px] truncate">{r.description || "—"}</TableCell>
                <TableCell className="text-[#C71E2E] font-ui">{fmtMoney(r.cost || 0)}</TableCell>
                <TableCell>
                  <span className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 whitespace-nowrap ${r.status === "Completed" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-700 border-amber-200 bg-amber-50"}`}>
                    {r.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button data-testid={`maintenance-delete-${r.id}`} onClick={() => remove(r)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Delete">
                    <Trash2 size={15} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
